import React, { useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import type { FundingRequestPrivateDto } from '@/api/kubik/credit'
import { useCreateLost } from '@/api/kubik/credit'
import { CustomizedForm } from '@/components/composed/forms'
import { useApplyingOfferFormSteps } from '@/components/composed/landingBlocks/deprecated/LandingApplyingOfferForm/useApplyingOfferFormSteps'
import { useAuthenticateUser } from '@/components/ui/AuthButton/hooks'
import { FormValuesFormatter } from '@/constants/financing'
import { isUserAgrarian } from '@/logic/auth/reducer'
import type { IFinancingOfferFormValues } from '@/logic/financing/interfaces'
import {
  formattedRequestBody,
  isMFOProducts,
} from '@/pages/Financing/components/FinancingProducts/components/LoanProducts/components/LoanProductsList/components/LoanProduct/components/LoanProductCollapsing/LoanProductOrder/components/LoanProductOrderForm/helpers'
import { EActiveBlock } from '@/pages/JournalPublication/components/PublicationText/components/PublicationFinancingWidget/components/PublicationFinancingWidgetCollapsingFinancingProductOrder/constants'
import type { RootState } from '@/redux/interfaces'
import { getApplyingFinancingOfferFormInitialValues } from '@/utils/getApplyingFinancingOfferFormInitialValues'
import { isApplyingOfferRequestBody } from '@/utils/hooks/applyingFinancingOffer/typeguard'
import { useApplyingFinancingOfferForm } from '@/utils/hooks/applyingFinancingOffer/useApplyingFinancingOfferForm'
import { widgetFinancingOfferStepsAdapter } from './helpers'
import { messages } from './messages'
import type { IPublicationFinancingWidgetFinancingOrderProps } from './types'
import './styles.scss'

const mapState = (state: RootState) => ({
  isAgrarian: isUserAgrarian()(state),
})

export const PublicationFinancingWidgetFinancingOrder: React.FC<IPublicationFinancingWidgetFinancingOrderProps> = props => {
  const {
    widgetType,
    setIsActiveBlock,
    createFundingRequest,
    isLoading,
    financingProductId,
    financingOfferType,
    setSuccessFundingRequestId,
  } = props

  const productId = financingProductId
  const offerType = financingOfferType

  const { isAgrarian } = useSelector(mapState)

  const isMFOProduct = isMFOProducts(productId)
  const { mutateAsync: createFundingRequestLost } = useCreateLost()
  const { isAuthenticated, openAuthModal } = useAuthenticateUser()
  const steps = useApplyingOfferFormSteps(offerType, isAuthenticated)

  const openSuccessBlock = (fundingRequests: FundingRequestPrivateDto[]) => {
    setSuccessFundingRequestId(fundingRequests[0]?.id)
    setIsActiveBlock(EActiveBlock.SUCCESS_FINANCING_OFFER)
  }

  const {
    dispatch,
    setOptions,
    formValues: values,
    profile,
  } = useApplyingFinancingOfferForm({ productId, offerType, shouldSkipSuccessModal: true, onSuccess: openSuccessBlock })
  const shouldDisableButton = isAuthenticated && !isAgrarian

  const openConsultForm = () => {
    setIsActiveBlock(EActiveBlock.CONSULTATION)
  }

  const formattedSteps = useMemo(
    () => {
      const offerTypeRelatedSteps = widgetFinancingOfferStepsAdapter(
        steps,
        { dispatch, onFormInteraction: () => {} },
        openConsultForm,
        isLoading,
      )

      return offerTypeRelatedSteps
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [steps],
  )

  const formattedStepsRef = useRef(formattedSteps)

  useEffect(() => {
    formattedStepsRef.current = formattedSteps
  }, [formattedSteps])

  const onSubmitForm = async (formValues: IFinancingOfferFormValues) => {
    const preparedFormValues: IFinancingOfferFormValues = { ...formValues, comment: messages.form.comment }
    const formattedFormValues = FormValuesFormatter[offerType]?.(preparedFormValues) || preparedFormValues

    if (isAuthenticated) {
      if (isApplyingOfferRequestBody(formattedFormValues)) {
        const formattedRequest = formattedRequestBody(formattedFormValues, isMFOProduct)

        await createFundingRequest({ ...formattedRequest, comment: messages.form.comment })
      }
    } else {
      setOptions({ formValues: {} })

      openAuthModal({
        shouldSendCodeImmediately: true,
        initialValues: { phone: formValues.phone, email: formValues.email, role: 'user' },
        onCloseDialog: () => {
          setOptions({ formValues: preparedFormValues })
        },
        onCloseModal: async () => {
          if (isApplyingOfferRequestBody(formattedFormValues)) {
            await createFundingRequestLost(formattedRequestBody(formattedFormValues, isMFOProduct))
          }
        },
        shouldSkipSelectRoleStep: true,
      })
    }
  }

  return (
    <div className="publicationFinancingWidgetFinancingOrder">
      <h3>{messages[widgetType].title}</h3>

      <div className="publicationFinancingWidgetFinancingOrder__wrapper">
        <div className="publicationFinancingWidgetFinancingOrder__text">{messages.common.description}</div>
        <div className="publicationFinancingWidgetFinancingOrder__form">
          <CustomizedForm<IFinancingOfferFormValues>
            initialValues={getApplyingFinancingOfferFormInitialValues({ offerType, productId, profile, formValues: values })}
            onSubmit={onSubmitForm}
            steps={formattedStepsRef.current}
            // TODO: убрать хранимое в redux состояние запроса https://jira.poleinvest.ru/browse/IFA-2085
            requestStatusName="mockedRequestStatus"
            shouldDisableButton={shouldDisableButton}
            shouldShowRequiringFieldsLabel={false}
            shouldUseLocalRequestState
          />
        </div>
      </div>
    </div>
  )
}
